import { useQueryClient } from '@tanstack/react-query';

import type {
  ClassGroupRes as ClassroomRes,
  ClassLinkRes,
  CreateClassGroupReq as CreateClassroomReq,
  JoinClassReq,
  JoinClassRes,
  LinkContentReq,
  UpdateClassGroupReq as UpdateClassroomReq,
  UpdateMemberReq,
} from '@/api/gen/_model';
import {
  ClassroomRoleResRole,
  type SuccessBodyClassGroupDetailRes,
  type SuccessBodyClassroomRoleRes,
} from '@/api/gen/_model';
import {
  getGetClassGroupDetailQueryKey,
  getGetClassroomRoleQueryKey,
  useCreateClassGroup as useCreateClassroomBase,
  useDeleteClassGroup as useDeleteClassroomBase,
  useDeleteClassLink as useDeleteClassLinkBase,
  useLinkContent as useLinkContentBase,
  useRemoveMember as useRemoveMemberBase,
  useUpdateClassGroup as useUpdateClassroomBase,
  useUpdateMember as useUpdateMemberBase,
} from '@/api/gen/classroom/classroom.gen';
import {
  useJoinClass as useJoinClassBase,
  useLeaveClassroom as useLeaveClassroomBase,
} from '@/api/gen/classroom-student/classroom-student.gen';
import {
  invalidateClassroomDetailQueries,
  invalidateClassroomListQueries,
  invalidateClassroomMutationQueries,
} from '@/shared/features/classroom/utils';

export const useCreateClassroom = () => {
  const qc = useQueryClient();
  const base = useCreateClassroomBase({
    mutation: {
      onSuccess: res => {
        const classroom = res.data;
        qc.setQueryData<SuccessBodyClassGroupDetailRes>(
          getGetClassGroupDetailQueryKey(classroom.classGroupId),
          {
            status: res.status,
            message: res.message,
            data: {
              ...classroom,
              links: [],
            },
          },
        );
        qc.setQueryData<SuccessBodyClassroomRoleRes>(
          getGetClassroomRoleQueryKey(classroom.classGroupId),
          {
            status: res.status,
            message: res.message,
            data: {
              classGroupId: classroom.classGroupId,
              role: ClassroomRoleResRole.OWNER,
            },
          },
        );

        return Promise.all([
          invalidateClassroomListQueries(qc),
          qc.invalidateQueries({
            queryKey: getGetClassGroupDetailQueryKey(classroom.classGroupId),
          }),
          qc.invalidateQueries({
            queryKey: getGetClassroomRoleQueryKey(classroom.classGroupId),
          }),
        ]);
      },
    },
  });
  return {
    ...base,
    mutate: (
      data: CreateClassroomReq,
      options?: {
        onSuccess?: (data: ClassroomRes) => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { data },
        {
          onSuccess: options?.onSuccess
            ? res => options.onSuccess?.(res.data)
            : undefined,
          onError: options?.onError,
        },
      ),
    mutateAsync: async (data: CreateClassroomReq): Promise<ClassroomRes> => {
      const res = await base.mutateAsync({ data });
      return res.data;
    },
  };
};

export const useUpdateClassroom = () => {
  const qc = useQueryClient();
  const base = useUpdateClassroomBase({
    mutation: {
      onSuccess: res =>
        Promise.all([
          invalidateClassroomListQueries(qc),
          qc.invalidateQueries({
            queryKey: getGetClassGroupDetailQueryKey(res.data.classGroupId),
          }),
        ]),
    },
  });

  return {
    ...base,
    mutate: (
      classroomId: string,
      data: UpdateClassroomReq,
      options?: {
        onSuccess?: (data: ClassroomRes) => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId, data },
        {
          onSuccess: options?.onSuccess
            ? res => options.onSuccess?.(res.data)
            : undefined,
          onError: options?.onError,
        },
      ),
    mutateAsync: async (
      classroomId: string,
      data: UpdateClassroomReq,
    ): Promise<ClassroomRes> => {
      const res = await base.mutateAsync({ classGroupId: classroomId, data });
      return res.data;
    },
  };
};

export const useDeleteClassroom = () => {
  const qc = useQueryClient();
  const base = useDeleteClassroomBase({
    mutation: {
      onSuccess: () => invalidateClassroomListQueries(qc),
    },
  });

  return {
    ...base,
    mutate: (
      classroomId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId },
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        },
      ),
    mutateAsync: (classroomId: string) =>
      base.mutateAsync({ classGroupId: classroomId }),
  };
};

export const useLinkContent = (classroomId: string) => {
  const qc = useQueryClient();
  const base = useLinkContentBase({
    mutation: {
      onSuccess: () => invalidateClassroomMutationQueries(qc, classroomId),
    },
  });
  return {
    ...base,
    mutate: (
      data: LinkContentReq,
      options?: {
        onSuccess?: (data: ClassLinkRes) => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId, data },
        {
          onSuccess: options?.onSuccess
            ? res => options.onSuccess?.(res.data)
            : undefined,
          onError: options?.onError,
        },
      ),
    mutateAsync: async (data: LinkContentReq): Promise<ClassLinkRes> => {
      const res = await base.mutateAsync({ classGroupId: classroomId, data });
      return res.data;
    },
  };
};

export const useDeleteClassLink = (classroomId: string) => {
  const qc = useQueryClient();
  const base = useDeleteClassLinkBase({
    mutation: {
      onSuccess: () => invalidateClassroomMutationQueries(qc, classroomId),
    },
  });
  return {
    ...base,
    mutate: (linkId: string) =>
      base.mutate({ classGroupId: classroomId, classLinkId: linkId }),
    mutateAsync: (linkId: string) =>
      base.mutateAsync({ classGroupId: classroomId, classLinkId: linkId }),
  };
};

export const useUpdateMember = (classroomId: string) => {
  const qc = useQueryClient();
  const base = useUpdateMemberBase({
    mutation: {
      onSuccess: () => invalidateClassroomDetailQueries(qc, classroomId),
    },
  });

  return {
    ...base,
    mutate: (
      userId: string,
      data: UpdateMemberReq,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId, userId, data },
        {
          onSuccess: options?.onSuccess
            ? () => options.onSuccess?.()
            : undefined,
          onError: options?.onError,
        },
      ),
    mutateAsync: (userId: string, data: UpdateMemberReq) =>
      base.mutateAsync({ classGroupId: classroomId, userId, data }),
  };
};

export const useRemoveMember = (classroomId: string) => {
  const qc = useQueryClient();
  const base = useRemoveMemberBase({
    mutation: {
      onSuccess: () => invalidateClassroomDetailQueries(qc, classroomId),
    },
  });

  return {
    ...base,
    mutate: (
      userId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId, userId },
        {
          onSuccess: options?.onSuccess
            ? () => options.onSuccess?.()
            : undefined,
          onError: options?.onError,
        },
      ),
    mutateAsync: (userId: string) =>
      base.mutateAsync({ classGroupId: classroomId, userId }),
  };
};

export const useJoinClassroom = (classroomId: string) => {
  const qc = useQueryClient();
  const base = useJoinClassBase({
    mutation: {
      onSuccess: () =>
        Promise.all([
          invalidateClassroomListQueries(qc),
          qc.invalidateQueries({
            queryKey: getGetClassroomRoleQueryKey(classroomId),
          }),
        ]),
    },
  });

  return {
    ...base,
    mutateAsync: async (data: JoinClassReq): Promise<JoinClassRes> => {
      const res = await base.mutateAsync({ classGroupId: classroomId, data });
      return res.data;
    },
  };
};

export const useLeaveClassroom = () => {
  const qc = useQueryClient();
  const base = useLeaveClassroomBase({
    mutation: {
      onSuccess: () => invalidateClassroomListQueries(qc),
    },
  });

  return {
    ...base,
    mutate: (
      classroomId: string,
      options?: {
        onSuccess?: () => void;
        onError?: (error: unknown) => void;
      },
    ) =>
      base.mutate(
        { classGroupId: classroomId },
        {
          onSuccess: options?.onSuccess,
          onError: options?.onError,
        },
      ),
    mutateAsync: (classroomId: string) =>
      base.mutateAsync({ classGroupId: classroomId }),
  };
};
